import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Markdown from "react-markdown";
import { useTranslation } from "react-i18next";
import {Modal, ModalHeader, ModalBody, ModalFooter} from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import DotsPopup from "../../components/Popup/DotsPopup";
import LoadingAnimation from "../../components/LoadingAnimation";
import TextLink from "../../components/TextLink";
import Checkbox from "../../components/Checkbox";
import taskIcon from "url:shared-assets/icons/task.svg";
import iconDate from "url:shared-assets/icons/calendar.svg";
import iconLearning from "url:shared-assets/icons/learning.svg";
import iconAi from "url:shared-assets/icons/ai.svg";
import iconAiBlack from "url:shared-assets/icons/black/ai.svg";
import iconBadgeCheck from "url:shared-assets/icons/green/badge-check-filled.svg";
import iconWarning from "url:shared-assets/icons/warning.svg";
import iconGreenCheck from "url:shared-assets/icons/green/checkmark.svg";
import Api from "../../services/Api";
import {getLanguage} from "../../lang/helpers";
import { formatDateYear } from "../../helpers/dateFunctions";

import iconPencilBare from "url:shared-assets/icons/pencil-bare.svg";
import iconTrash from "url:shared-assets/icons/trash.svg";

import TabMenu from "../../components/TabMenu/TabMenu";
import { getTabNr, setTabNr } from "../../helpers/tabNavigation";
import { FeedbackInterface, TaskInterface } from "./interfaces";
import Feedback from "./components/Feedback";
import { storeAiStart } from "../../helpers/ai_thread";
import LocalBreadcrumbs from "../../components/Breadcrumbs/LocalBreadcrumbs";
import remarkGfm from "remark-gfm";
import AttentionBox from "../../components/Boxes/AttentionBox";
import {attentionTxt} from "../../helpers/misc_texts";

export default function TaskPage() {
  const api = Api.getInstance();
  const { taskId } = useParams();
  const navigate = useNavigate();
  const translation = useTranslation();
  const { t, i18n } = translation;
  const lang = getLanguage(i18n.language);
  const [task, setTask] = useState<TaskInterface | null>(null);
  const [feedbacks, setFeedbacks] = useState<Array<FeedbackInterface>>([]);
  const [categories, setCategories] = useState(0);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [completed, setCompleted] = useState<boolean>(false);
  const [showCompletedModal, setShowCompletedModal] = useState<boolean>(false);
  const [askDeleteTask, setAskDeleteTask] = useState<boolean>(false);
  const [tab, setTab] = useState(getTabNr());
  const now = new Date();

  useEffect(() => {
    getTask();
  }, []);

  const getTask = () => {
    api.get("client/task/" + taskId + "?feedback=1").then((response) => {
      setTask(response.json.task);
      setCompleted(response.json.task.completed);
      setFeedbacks(response.json.feedbacks);
      setCategories(response.json.categories);
      setLoaded(true);
    }).catch((error) => {
      if (error.response && error.response.status === 404) {
        setLoaded(true);
      } else {
        console.log(error)
      }
    });
  };

  const changeTab = (tab: number) => {
    setTab(tab);
    setTabNr(tab);
  };

  const deleteTask = () => {
    const data = {
      remove: true,
    };
    api.post("client/tasks/" + taskId + "/edit", data).then((response) => {
      navigate("/dev-plan");
    });
  };

  const changeCompletion = () => {
    const newCompleted = !completed;
    const data = {
      completed: newCompleted,
    };
    api.post("client/tasks/" + task?.id + "/update", data).then((response) => {
      setCompleted(newCompleted);
      setShowCompletedModal(newCompleted);
    });
  };

  const showCompletedOption = () => {
    if (task?.completed && task.feedback) {
      return null;
    }
    let label: string;
    if (completed) {
      label = lang === "sv" ? "Avmarkera som klar" : "Undo completion";
    } else {
      label = lang === "sv" ? "Markera som klar" : "Mark as completed";
    }

    return (
      <>
        <Checkbox
          id="1"
          label={label}
          checked={completed}
          labelClassName="z-title3"
          onChange={() => changeCompletion()}
        />

        <hr className="border-indigo-700 my-4"/>
      </>
    );
  };

  if (!loaded) {
    return (
      <div className="w-full h-full flex justify-center mt-4 desktop:mt-6">
        <div className="flex flex-col gap-2 justify-center align-middle w-full desktop:w-151 m-4 mt-0">
          <LoadingAnimation/>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="main-one-col small">
        <div className="z-h2 mt-6">
          <div className="flex justify-center">
            <img
              src={taskIcon}
              alt="task icon"
              className="w-6 h-6 desktop:w-10 desktop:h-10"
            />
          </div>
          <p className="z-body1 text-center">
            {lang === 'sv' ? 'Hittade inte aktivitet med detta id.' : 'Could not find the activity with this id.'}
          </p>
          <CustomButton
            className="mt-8"
            onClick={() => navigate("/development-plan")}
          >
            {t("go-to-focus-area")}
          </CustomButton>
        </div>
      </div>
    );
  }

  let popItems = [
    {
      title: lang === "sv" ? "Ändra" : "Edit",
      click: () => {
        navigate("/tasks/" + task?.id + "/edit");
      },
      img: <img src={iconPencilBare} alt="edit" className="h-4 w-4" />,
    },
    {
      title: t("delete"),
      click: () => setAskDeleteTask(true),
      img: <img src={iconTrash} alt="delete" className="h-4 w-4" />,
    },
  ];

  const renderContinueChat = () => {
    if (task.originalChatThreadId) {
      if (completed) {
        const msg =
          lang === "sv"
            ? "Jag vill reflektera över den aktivitet jag avslutat: " +
              task.name
            : "I would like to reflect on the Action I completed: " + task.name;
        storeAiStart(
          "coaching",
          "action",
          null,
          task.id,
          task.originalChatThreadId,
          msg
        );
      }
      return (
        <CustomButton
          buttonType="outlineWhiteBase"
          className="mt-2 flex items-center justify-center"
          leftIcon={
            <img src={iconAi} alt="AI" className="h-4 w-4 rounded-full" />
          }
          onClick={() => navigate("/ai/" + task.originalChatThreadId)}
        >
          {completed
            ? lang === "sv"
              ? "Reflektera kring aktivitet"
              : "Reflect on Action"
            : lang === "sv"
            ? "Fortsätt med coach-session"
            : "Continue with Coach session"}
        </CustomButton>
      );
    }
    return null;
  };

  const renderDetails = () => {
    return (
      <>
        {completed && (
          <div className="flex items-center mt-6 border border-success-500 rounded-md p-4 bg-indigo-800">
            <img src={iconGreenCheck} alt="check" />
            <p className="ml-2 z-title3">
              {lang === "sv" ? "Aktivitet klar" : "Completed"}
            </p>
          </div>
        )}

        {!completed && new Date(task.deadline) < now &&
            <AttentionBox message={attentionTxt(lang, 'task', false)} />
        }

        <div className="bg-indigo-900 rounded-md p-4 mt-4">
          {showCompletedOption()}

          <div className="flex items-center">
            <img src={iconDate} alt="date" />
            <div className="ml-2">
              {t("due-date")}:&nbsp;
              <span className="text-gray-400">
                {task.deadline ? formatDateYear(task.deadline) : "-"}
              </span>
            </div>
          </div>

          {task.focusAreaId && (
            <div className="mt-2 flex items-center">
              <img src={iconLearning} alt="fa" className="h-4 w-4" />
              <div className="ml-2">{t("focus-area-nav-title")}:</div>
              <div className="ml-2">
                <TextLink
                  onClick={() =>
                    navigate("/development-plan/focus-area/" + task.focusAreaId)
                  }
                >
                  {task.focusAreaName}
                </TextLink>
              </div>
            </div>
          )}

          <hr className="my-4 border-indigo-700" />

          <div className="markdown-content text-gray-300">
            <Markdown remarkPlugins={[remarkGfm]}>
              {task.description}
            </Markdown>
          </div>

          <div className="h-4">&nbsp;</div>
          {renderContinueChat()}
        </div>

        {showCompletedModal && (
          <Modal isOpen={true} onClose={() => setShowCompletedModal(false)}>
            <ModalHeader onClose={() => setShowCompletedModal(false)}>
              {lang === "sv" ? "Aktivitet klar" : "Action completed"}
            </ModalHeader>
            <ModalBody>
              <img
                src={iconBadgeCheck}
                alt="check"
                className="h-12 w-12 mx-auto mt-3"
              />
              <div className="mt-6">
                {lang === "sv"
                  ? "Bra jobbat! Du gör framsteg. Ta en stund och reflektera över vad du har lärt dig och be om feedback för att bekräfta dina framsteg."
                  : "Great job! You’re making progress. Take a moment to reflect on what you’ve learned and request feedback to validate your achievement."}
              </div>
            </ModalBody>
            <ModalFooter>
              <div className="w-full">
                {renderContinueChat()}
              </div>
            </ModalFooter>
          </Modal>
        )}
        {askDeleteTask && (
          <Modal isOpen={true} onClose={() => setAskDeleteTask(false)}>
            <ModalHeader onClose={() => setAskDeleteTask(false)}>
              {lang === "sv" ? "Ta bort aktivitet?" : "Delete Action?"}
            </ModalHeader>
            <ModalBody>
              {lang === "sv"
                ? "Detta kommer permanent ta bort aktiviteten "
                : "This will permanently delete Action "}
              {task?.name}
            </ModalBody>
            <ModalFooter>
              <div className="w-full">
                <CustomButton
                  buttonType="outlineWhiteBase"
                  onClick={() => setAskDeleteTask(false)}
                >
                  {t("cancel")}
                </CustomButton>

                {
                  <CustomButton
                    buttonType="dangerBase"
                    className="mt-2"
                    onClick={() => deleteTask()}
                  >
                    {t("delete")}
                  </CustomButton>
                }
              </div>
            </ModalFooter>
          </Modal>
        )}
      </>
    );
  };

  const renderTab = () => {
    if (tab === 1) {
      return <Feedback task={task} feedbacks={feedbacks} />;
    }
    return renderDetails();
  };

  const showCrumbs = () => {

    let taskName = lang === "sv" ? "Aktivitet" : "Action"

    if (categories > 0) {
      return (
        <LocalBreadcrumbs crumbs={[
          {name: t("long-term-goals"), url: "/dev-plan"},
          {name: taskName},
        ]}/>
      )
    }
    return (
      <LocalBreadcrumbs crumbs={[
        {name: lang === "sv" ? "Hem" : "Home", url: "/"},
        {name: lang === "sv" ? "Aktiviteter" : "Activities", url: "/tasks"},
        {name: taskName},
      ]}/>
    )
  }

  return (
    <div className="main-content">
      {showCrumbs()}
      <div className="flex my-4 desktop:mt-6 justify-between items-center">
        <div className="flex gap-x-2">
          <div className="bg-peach-600 rounded-md h-12 w-12 flex items-center justify-center">
            <img src={iconAiBlack} className="h-6 w-6" alt="ico"/>
          </div>
          <div>
            <div className="text-sm text-medium text-peach-600">
              {lang === "sv" ? "Fristående AI coaching" : "Standalone AI coaching"}
            </div>
            <div className="z-heading2">{task.name}</div>
          </div>
        </div>
        <DotsPopup items={popItems} noHover={true} />
      </div>


      {
        task.feedback &&
          <TabMenu
              active={tab}
              items={[
                {
                  title: t("description"),
                  callback: () => changeTab(0),
                },
                {
                  title: t("feedback"),
                  callback: () => changeTab(1),
                },
              ]}
          />
      }

      {renderTab()}
    </div>
  );
}
